import { useState, type ComponentProps } from "react";
import { BulkBar, Button, CustomerTable, Dialog, Menu, type CustomerRowData } from "@omnabase/ui";
import type { Tag } from "@omnabase/shared";
import { useCan } from "../../lib/access";
import { api, ApiError } from "../../lib/api";
import { useMediaQuery } from "../../lib/inbox";
import { useTicked } from "../../lib/useTicked";
import { useSession } from "../../shell/store";

/** The customers table's bar (2026-10-20; DECISIONS "2026-10-20 — Logos, Legal & data…", decisions 1 and 2): Block (every ticked customer's
 *  address goes to Blocked senders, one Undo) and, for the owner and Administrators, "Delete…" (a dialog naming the count, the count typed to
 *  confirm, one deletion job per customer, no Undo; the rows leave the list at once). The customer tags round (2026-10-24): "Tag" and
 *  "Remove tag", each a menu of the customer tags, for whoever may edit customers. */
type Result = { changed: number; summary: string; undoId: string | null };
type TableProps = Omit<ComponentProps<typeof CustomerTable>, "rows" | "onToggle" | "onToggleAll" | "selectable">;

/** The customers table with its ticks (as the tickets table: a row's box, shift for a range, the head's box, the bar), none on a phone */
export function SelectableCustomers({ slug, rows, reload, customerTags = [], ...table }: TableProps & { slug: string; rows: CustomerRowData[]; reload: () => Promise<unknown> | void; /** the customer tags, for the bar's Tag and Remove tag */ customerTags?: Tag[] }) {
  const phone = useMediaQuery("(max-width: 767px)");
  const ticked = useTicked(rows.map((r) => r.id));
  const shown = phone ? rows : rows.map((r) => ({ ...r, checked: ticked.has(r.id) }));
  return (
    <>
      <CustomerTable {...table} rows={shown} selectable={!phone} onToggle={(id, on, range) => ticked.tick(id, on, range)} onToggleAll={(on) => (on ? ticked.all() : ticked.clear())} />
      {!phone && ticked.ids.length > 0 && <CustomersBulkBar slug={slug} ids={ticked.ids} total={rows.length} onSelectAll={ticked.all} onClear={ticked.clear} reload={reload} customerTags={customerTags} />}
    </>
  );
}
type Props = { slug: string; ids: string[]; total: number; onSelectAll: () => void; onClear: () => void; reload: () => Promise<unknown> | void; customerTags?: Tag[] };

export function CustomersBulkBar({ slug, ids, total, onSelectAll, onClear, reload, customerTags = [] }: Props) {
  const { workspace, toast } = useSession(); const mayTag = useCan()("customers") && customerTags.length > 0;
  const [deleting, setDeleting] = useState<string[] | null>(null);
  const mayDelete = workspace.me.role === "owner" || workspace.me.role === "admin";
  const block = async () => {
    const chosen = ids; onClear();
    try {
      const r = await api.post<Result>(`/api/v1/w/${slug}/customers/bulk`, { ids: chosen, action: "block" });
      await reload();
      toast(r.summary, { kind: "success", action: r.undoId ? { label: "Undo", onClick: () => void undo(r.undoId!) } : undefined });
    } catch (e) { toast(e instanceof ApiError ? e.message : "Could not block them", { kind: "error" }); }
  };
  const undo = async (undoId: string) => {
    try { const u = await api.post<{ restored: number; left: unknown[] }>(`/api/v1/w/${slug}/customers/bulk/undo`, { undoId }); await reload(); if (u.left.length) toast(`${u.restored} unblocked · ${u.left.length} changed since`, { kind: "success" }); }
    catch (e) { toast(e instanceof ApiError ? e.message : "Could not take it back", { kind: "error" }); }
  };
  const tag = async (action: "tag" | "untag", tagId: string) => {
    const chosen = ids; onClear();
    try { const r = await api.post<Result>(`/api/v1/w/${slug}/customers/bulk`, { ids: chosen, action, tag: tagId }); await reload(); toast(r.summary, { kind: "success" }); }
    catch (e) { toast(e instanceof ApiError ? e.message : "Could not change the tags", { kind: "error" }); }
  };
  const tagMenu = (action: "tag" | "untag", label: string) => <Menu align="end" trigger={<Button variant="ghost" size="sm" icon="tag">{label}</Button>} sections={[{ label: `${label} ${ids.length}`, items: customerTags.map((t) => ({ id: t.id, label: t.name, dot: `var(--ob-tag-${t.color ?? "gray"})`, onSelect: () => void tag(action, t.id) })) }]} />;
  return (
    <>
      <BulkBar count={ids.length} total={total} onSelectAll={onSelectAll} onClear={onClear}>
        {mayTag && <>{tagMenu("tag", "Tag")}{tagMenu("untag", "Remove tag")}</>}
        <Button variant="ghost" size="sm" icon="spam" onClick={() => void block()}>Block</Button>
        {mayDelete && <Button variant="ghost" size="sm" icon="trash" onClick={() => setDeleting(ids)}>Delete…</Button>}
      </BulkBar>
      {deleting && <DeleteCustomersDialog slug={slug} ids={deleting} onClose={() => setDeleting(null)} onDone={async () => { setDeleting(null); onClear(); await reload(); }} />}
    </>
  );
}

const many = (n: number) => `${n} ${n === 1 ? "customer" : "customers"}`;

/** The bulk deletion's one question (decision 1): it names the count and asks for it typed, as the single deletion asks for the address */
function DeleteCustomersDialog({ slug, ids, onClose, onDone }: { slug: string; ids: string[]; onClose: () => void; onDone: () => Promise<void> }) {
  const { toast } = useSession();
  const [typed, setTyped] = useState(""); const [busy, setBusy] = useState(false);
  const armed = typed.trim() === String(ids.length);
  const submit = async () => {
    if (!armed || busy) return; setBusy(true);
    try { const r = await api.post<Result>(`/api/v1/w/${slug}/customers/bulk`, { ids, action: "delete", confirm: ids.length }); toast(r.summary); await onDone(); }
    catch (e) { toast(e instanceof ApiError ? e.message : "Could not start the deletion", { kind: "error" }); setBusy(false); }
  };
  return (
    <Dialog title={`Delete ${many(ids.length)}?`} onClose={onClose} intro={`Their tickets, messages, files, notes and licences are deleted for good, ${many(ids.length)} in all. There is no undo.`}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!armed || busy} onClick={() => void submit()}>{`Delete ${many(ids.length)}`}</Button></>}>
      <div className="ob-dialog__field"><span className="ob-dialog__label">Type {ids.length} to confirm</span><input className="ob-dialog__input" inputMode="numeric" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={String(ids.length)} aria-label="Number of customers" autoFocus /></div>
    </Dialog>
  );
}
